import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Warning, CheckCircle, Eye, Funnel } from '@phosphor-icons/react';
import AdminLayout from '../components/AdminLayout';
import API_URL from '../config';

export default function AdminMonitoring() {
  const [data, setData] = useState({ packages: [], quarters: [], no_account_count: 0 });
  const [quarter, setQuarter] = useState('');
  const [unviewedOnly, setUnviewedOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (quarter) params.quarter = quarter;
      if (unviewedOnly) params.unviewed_needslook = true;
      const res = await axios.get(`${API_URL}/api/admin/monitoring/packages`, { params });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load monitoring packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [quarter, unviewedOnly]);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-monitoring">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#141b4d' }}>Quarterly Monitoring</h1>
          <p className="text-sm mt-1" style={{ color: '#71717A' }}>
            Packages received from the SRL Console. {data.no_account_count > 0 &&
              <span style={{ color: '#B45309' }}>{data.no_account_count} with no portal account.</span>}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <select value={quarter} onChange={(e) => setQuarter(e.target.value)}
            data-testid="monitoring-quarter-filter" className="border border-[#E4E4E7] px-3 py-2 text-sm">
            <option value="">All quarters</option>
            {data.quarters.map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
          <button onClick={() => setUnviewedOnly(!unviewedOnly)}
            data-testid="monitoring-unviewed-filter"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold border"
            style={unviewedOnly
              ? { background: '#141b4d', color: '#fff', borderColor: '#141b4d' }
              : { background: '#fff', color: '#141b4d', borderColor: '#E4E4E7' }}>
            <Funnel weight="bold" className="w-4 h-4" /> Unviewed with "needs a look"
          </button>
        </div>

        {loading ? <p className="text-sm text-[#71717A]">Loading…</p> : (
          <div className="overflow-hidden border border-[#E4E4E7]">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF8]">
                <tr className="text-left" style={{ color: '#71717A' }}>
                  <th className="px-3 py-2">Quarter</th>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Account</th>
                  <th className="px-3 py-2">Needs a look</th>
                  <th className="px-3 py-2">Viewed</th>
                </tr>
              </thead>
              <tbody data-testid="monitoring-packages-table">
                {data.packages.map((p) => (
                  <tr key={p.id} className="border-t border-[#F4F4F5]">
                    <td className="px-3 py-2 font-medium" style={{ color: '#09090B' }}>{p.quarter}</td>
                    <td className="px-3 py-2" style={{ color: '#09090B' }}>
                      {p.client_name || p.client_email || <span style={{ color: '#A1A1AA' }}>({p.hubspot_contact_id})</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.has_portal_account
                        ? <span style={{ color: '#16A34A' }}>Yes</span>
                        : <span className="font-semibold" style={{ color: '#B45309' }}>No portal account</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.has_needslook
                        ? <span className="inline-flex items-center gap-1 font-semibold" style={{ color: '#DC2626' }}>
                            <Warning weight="fill" className="w-4 h-4" /> Yes</span>
                        : <span style={{ color: '#A1A1AA' }}>—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.viewed
                        ? <span className="inline-flex items-center gap-1" style={{ color: '#16A34A' }}>
                            <CheckCircle weight="fill" className="w-4 h-4" /> Viewed</span>
                        : <span className="inline-flex items-center gap-1" style={{ color: '#71717A' }}>
                            <Eye weight="bold" className="w-4 h-4" /> Not yet</span>}
                    </td>
                  </tr>
                ))}
                {data.packages.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center" style={{ color: '#A1A1AA' }}>No packages.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
